import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Profile } from '../../types/auth.types';
import { supabase } from '../../lib/supabaseClient';
import { SubscriptionStatus, SubscriptionStatusType } from '../../types/billing.types';
import { Sparkles, ShieldCheck, ArrowUpRight, RotateCcw } from 'lucide-react';

function initials(fullName: string): string {
  return fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

function calculateLevel(xp: number): { level: number; title: string; nextLevelXp: number } {
  const level = Math.floor(xp / 250) + 1;
  const nextLevelXp = level * 250;
  const titles = ['Çırak Kodcu', 'Kod Kaşifi', 'Kod Ustası', 'Kod Üstadı', 'Kod Efsanesi'];
  const title = titles[Math.min(titles.length - 1, Math.floor(level / 5))];
  return { level, title, nextLevelXp };
}

interface ProfileHeaderCardProps {
  profile: Profile;
}

interface UserSubscription {
  status: SubscriptionStatusType;
  plan_name: string;
  current_period_end: string | null;
  ends_at: string | null;
  grace_period_end: string | null;
}

export function ProfileHeaderCard({ profile }: ProfileHeaderCardProps) {
  const { level, title, nextLevelXp } = calculateLevel(profile.xp);
  const percent = Math.min(100, Math.round((profile.xp / nextLevelXp) * 100));
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [isSubLoading, setIsSubLoading] = useState(true);
  const [subError, setSubError] = useState<string | null>(null);

  const loadSubscription = useCallback(async () => {
    if (!profile?.id) return;
    setIsSubLoading(true);
    setSubError(null);

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('status, plan_name, current_period_end, ends_at, grace_period_end')
        .eq('user_id', profile.id)
        .in('status', [SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING, SubscriptionStatus.PAST_DUE])
        .order('current_period_end', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn('[ProfileHeaderCard] Abonelik sorgu uyarısı (güvenli fallback devrede):', error.message);
        // DB veya enum uyuşmazlığı durumunda sayfanın çökmesini engelle
        setSubscription(null);
        setSubError('Abonelik bilgisi güncellenemedi.');
      } else if (data) {
        setSubscription(data as unknown as UserSubscription);
      } else {
        setSubscription(null);
      }
    } catch (err: any) {
      console.warn('[ProfileHeaderCard] Abonelik yakalanan hata (güvenli fallback devrede):', err);
      setSubscription(null);
      setSubError('Abonelik bağlantı hatası.');
    } finally {
      setIsSubLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const now = new Date();
  const effectiveEnd = subscription?.grace_period_end && new Date(subscription.grace_period_end) > now
    ? subscription.grace_period_end
    : (subscription?.current_period_end || subscription?.ends_at);
  const isPeriodValid = !effectiveEnd || new Date(effectiveEnd) > now;
  const hasActiveSub = Boolean(
    subscription &&
    isPeriodValid &&
    [SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING, SubscriptionStatus.PAST_DUE].includes(subscription.status)
  );
  const isGracePeriod = subscription?.status === SubscriptionStatus.PAST_DUE || Boolean(
    subscription?.grace_period_end &&
    new Date(subscription.grace_period_end) > now &&
    (subscription?.current_period_end || subscription?.ends_at) &&
    new Date(subscription.current_period_end || subscription.ends_at!) <= now
  );

  const planDisplayName = subscription?.plan_name
    ? `${subscription.plan_name}${isGracePeriod ? ' (Yenileme Bekleniyor)' : ''}`
    : (profile.role === 'admin' ? 'Yönetici' : 'Ücretsiz Başlangıç');

  const endDate = effectiveEnd;
  const formattedEndDate = endDate
    ? new Date(endDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const [copied, setCopied] = useState(false);

  const copyStudentCode = () => {
    if (profile.student_code) {
      navigator.clipboard.writeText(profile.student_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="card profile-header-card">
      <div className="profile-avatar-large">{initials(profile.full_name)}</div>
      <h2 className="profile-user-name">{profile.full_name}</h2>
      <p className="profile-user-level-title">
        Seviye {level} • <span className="profile-title-highlight">{title}</span>
      </p>

      {/* Dinamik Aktif Üyelik Planı Kartı */}
      <div className={`profile-plan-badge-card ${hasActiveSub ? 'active-premium-card' : 'free-plan-card'}`}>
        <div className="plan-badge-header">
          <div className="plan-badge-title-wrap">
            {hasActiveSub ? <Sparkles size={18} className="plan-sparkle-icon" /> : <ShieldCheck size={18} />}
            <span className="plan-badge-name">{planDisplayName}</span>
          </div>
          {hasActiveSub ? (
            <span className="plan-status-pill active">🟢 Aktif Plan</span>
          ) : (
            <Link to="/pricing" className="plan-upgrade-link">
              <span>Yükselt</span>
              <ArrowUpRight size={14} />
            </Link>
          )}
        </div>

        {hasActiveSub && formattedEndDate && (
          <div className="plan-badge-footer">
            <span>📅 Bitiş / Yenilenme: <strong>{formattedEndDate}</strong></span>
          </div>
        )}

        {subError && !hasActiveSub && (
          <div className="plan-badge-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
            <span style={{ fontSize: '11px', color: '#94a3b8' }}>Abonelik durumu güncellenemedi</span>
            <button
              type="button"
              onClick={() => loadSubscription()}
              disabled={isSubLoading}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary, #6366f1)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                fontWeight: 600,
                padding: '2px 6px',
              }}
              title="Yeniden dene"
            >
              <RotateCcw size={12} />
              <span>{isSubLoading ? 'Deneniyor...' : 'Tekrar Dene'}</span>
            </button>
          </div>
        )}
      </div>

      <div className="profile-interest-tags" style={{ marginTop: '14px' }}>
        {profile.skill_level && (
          <span className="lesson-badge primary">
            {profile.skill_level === 'beginner' ? '🌱 Başlangıç Düzeyi' : profile.skill_level === 'intermediate' ? '🚀 Orta Düzey' : '🔥 İleri Düzey'}
          </span>
        )}
      </div>

      <div className="progress-bar profile-xp-bar">
        <div className="progress-bar-fill" style={{ width: `${percent}%` }} />
      </div>
      <p className="profile-xp-label">
        <strong>{profile.xp.toLocaleString('tr-TR')}</strong> / {nextLevelXp.toLocaleString('tr-TR')} XP
      </p>

      {/* Dinamik Profil Detay Listesi */}
      <div className="profile-details-list">
        {profile.student_code && (
          <div className="profile-detail-item">
            <span>🎟️</span>
            <strong>Öğrenci Kodu:</strong>
            <span className="profile-code-badge" onClick={copyStudentCode} title="Kopyalamak için tıkla">
              {profile.student_code} {copied ? '✓ Kopyalandı' : '📋'}
            </span>
          </div>
        )}

        <div className="profile-detail-item">
          <span>🪙</span>
          <strong>Coin Bakiyesi:</strong>
          <span style={{ color: '#d97706', fontWeight: '800' }}>{(profile.coins ?? 0).toLocaleString('tr-TR')} Coin</span>
        </div>

        <div className="profile-detail-item">
          <span>⚡</span>
          <strong>Lig Puanı:</strong>
          <span style={{ color: '#0284c7', fontWeight: '800' }}>{(profile.league_xp ?? profile.xp).toLocaleString('tr-TR')} Puan</span>
        </div>

        {profile.age && (
          <div className="profile-detail-item">
            <span>🎂</span>
            <strong>Yaş:</strong> <span>{profile.age} yaşında</span>
          </div>
        )}

        {profile.school_name && (
          <div className="profile-detail-item">
            <span>🏫</span>
            <strong>Okul:</strong> <span>{profile.school_name}</span>
          </div>
        )}

        {profile.city && (
          <div className="profile-detail-item">
            <span>📍</span>
            <strong>Şehir:</strong> <span>{profile.city.toUpperCase()}</span>
          </div>
        )}

        {profile.interests && profile.interests.length > 0 && (
          <div className="profile-interests-section">
            <span className="profile-interests-title">🎯 İlgi Alanları</span>
            <div className="profile-interest-badges">
              {profile.interests.map((interest, idx) => (
                <span key={idx} className="lesson-badge secondary" style={{ fontSize: 12, padding: '4px 10px' }}>
                  {interest}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
